import { makeAutoObservable, runInAction } from 'mobx';
import type { WeatherDto } from '~/api/types/sync';

/**
 * Clima atual da cidade-referência da loja (Fase 1.0/1.5). Recebido no payload
 * de sync (bloco weather{}), cacheado local (offline-first — sobrevive
 * reload/offline). O player NUNCA busca clima direto; só consome o que o
 * servidor distribui. O texto/ícone é canônico PT-BR; provider é atribuição.
 */
export interface WeatherData {
  city: string | null;
  uf: string | null; // UF/estado da cidade da loja — usado pelo estilo Barra
  temp: number | null;
  feelsLike: number | null;
  condition: string | null; // código canônico interno (CLEAR, RAIN, ...)
  conditionPt: string | null;
  icon: string | null; // clear|partly|cloudy|rain|storm|snow|fog|wind
  humidity: number | null;
  windKmh: number | null;
  cloudsPct: number | null;
  rainMm1h: number | null;
  rainProb: number | null;
  provider: string | null;
  refCity: string | null;
  refDistanceKm: number | null;
  fetchedAt: string | null;
  expiresAt: string | null;
}

const CACHE_KEY = '@efflux:weather';

function normalize(dto: WeatherDto): WeatherData {
  return {
    city: dto.city ?? null,
    uf: dto.uf ?? null,
    temp: dto.temp ?? null,
    feelsLike: dto.feels_like ?? null,
    condition: dto.condition ?? null,
    conditionPt: dto.condition_pt ?? null,
    icon: dto.icon ?? null,
    humidity: dto.humidity ?? null,
    windKmh: dto.wind_kmh ?? null,
    cloudsPct: dto.clouds_pct ?? null,
    rainMm1h: dto.rain_mm_1h ?? null,
    rainProb: dto.rain_prob ?? null,
    provider: dto.provider ?? null,
    refCity: dto.ref_city ?? null,
    refDistanceKm: dto.ref_distance_km ?? null,
    fetchedAt: dto.fetched_at ?? null,
    expiresAt: dto.expires_at ?? null,
  };
}

class WeatherStore {
  current: WeatherData | null = null;

  constructor() {
    makeAutoObservable(this);
    this.loadFromCache();
  }

  /**
   * Atualiza a partir do sync. Se vier null/undefined (sem snapshot no server),
   * MANTÉM o último conhecido (offline-first — não pisca/some). Só substitui
   * quando chega dado novo.
   */
  setFromSync(dto: WeatherDto | null | undefined): void {
    if (!dto) return;
    const data = normalize(dto);
    runInAction(() => {
      this.current = data;
    });
    this.persist(data);
  }

  get hasWeather(): boolean {
    return this.current !== null;
  }

  private persist(data: WeatherData): void {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    } catch {
      /* storage cheio/indisponível — ignora, dado vive em memória */
    }
  }

  private loadFromCache(): void {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (raw) {
        this.current = JSON.parse(raw) as WeatherData;
      }
    } catch {
      /* cache corrompido — ignora */
    }
  }
}

export const weatherStore = new WeatherStore();
export default WeatherStore;
